import React, { useState, useEffect, useMemo, useRef } from 'react';
import axios from 'axios';
import { ShoppingCart, Soup, Utensils, Salad, Wheat, X, Check } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import Footer from '../components/Footer';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const RESERVATION_DISPLAY_SECONDS = 360; // 6-minute countdown shown in UI
const STOCK_URGENCY_THRESHOLD = 4;    // show low-stock badge only when ≤ this many portions left

const formatCountdown = (seconds) => {
  if (seconds <= 0) return 'Rezervat: 00:00 min';
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `Rezervat: ${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')} min`;
};

const getCategoryIcon = (category) => {
  switch (category) {
    case 'Ciorbe':
      return <Soup className="w-5 h-5" />;
    case 'Fel Principal':
      return <Utensils className="w-5 h-5" />;
    case 'Garnituri':
      return <Wheat className="w-5 h-5" />;
    case 'Salate':
      return <Salad className="w-5 h-5" />;
    default:
      return <Utensils className="w-5 h-5" />;
  }
};

const categories = [
  { value: 'Oferta zilei', label: 'Oferta zilei' },
  { value: 'Ciorbe', label: 'Ciorbe' },
  { value: 'Combo', label: 'Combo' },
  { value: 'Specialități', label: 'Specialități' },
  { value: 'Vegetarian', label: 'Vegetarian' },
  { value: 'Paste / Salate', label: 'Paste / Salate' },
  { value: 'Clătite', label: 'Clătite' }
];

// Human-readable Romanian day labels
const DAY_LABELS = { luni: 'Luni', marti: 'Marți', miercuri: 'Miercuri', joi: 'Joi', vineri: 'Vineri' };

// Returns today's date as YYYY-MM-DD in Romanian timezone
function getTodayRoDate() {
  const nowRo = new Date(new Date().toLocaleString('en-US', { timeZone: 'Europe/Bucharest' }));
  const y = nowRo.getFullYear();
  const m = String(nowRo.getMonth() + 1).padStart(2, '0');
  const d = String(nowRo.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/**
 * Returns a rich status object for 'Oferta zilei' ordering on the given day.
 *
 * States:
 *  • FUTURE DAY  – canOrder=true,  badgeType='info',    badge "Comenzi deschise până {Day} la 12:00"
 *  • TODAY pre-cutoff – canOrder=true,  badgeType='warning', badge "Comenzile se închid la 12:00"
 *  • TODAY post-cutoff– canOrder=false, badgeType='closed',  badge "Comenzile s-au închis la 12:00"
 */
function getOfertaZileiStatus(day) {
  const selectedDateStr = getDateForDay(day);
  const todayStr = getTodayRoDate();

  if (selectedDateStr !== todayStr) {
    // Future (or past) day — always allow; show informational pre-order badge
    return {
      canOrder: true,
      isToday: false,
      badgeText: `Comenzi deschise până ${DAY_LABELS[day] || day} la 12:00`,
      badgeType: 'info',
    };
  }

  // Today — check 12:00 RO cutoff
  const nowRo = new Date(new Date().toLocaleString('en-US', { timeZone: 'Europe/Bucharest' }));
  const cutoff = new Date(nowRo);
  cutoff.setHours(12, 0, 0, 0);

  if (nowRo < cutoff) {
    return {
      canOrder: true,
      isToday: true,
      badgeText: 'Comenzile se închid la 12:00',
      badgeType: 'warning',
    };
  }

  return {
    canOrder: false,
    isToday: true,
    badgeText: 'Comenzile s-au închis la 12:00',
    badgeType: 'closed',
  };
}

const daysOfWeek = [
  { value: 'luni', label: 'Luni' },
  { value: 'marti', label: 'Marți' },
  { value: 'miercuri', label: 'Miercuri' },
  { value: 'joi', label: 'Joi' },
  { value: 'vineri', label: 'Vineri' }
];

function getDateForDay(dayName) {
  // Use Romania-timezone "today" so the diff is computed correctly near midnight
  const nowRo = new Date(new Date().toLocaleString('en-US', { timeZone: 'Europe/Bucharest' }));
  const currentDay = nowRo.getDay(); // 0=Sun … 6=Sat, in RO tz
  const daysMap = { luni: 1, marti: 2, miercuri: 3, joi: 4, vineri: 5 };
  const targetDay = daysMap[dayName];

  let diff = targetDay - currentDay;
  if (diff < 0) diff += 7;

  const targetDate = new Date(nowRo);
  targetDate.setDate(nowRo.getDate() + diff);
  // Return YYYY-MM-DD in RO local date (not UTC)
  const y = targetDate.getFullYear();
  const m = String(targetDate.getMonth() + 1).padStart(2, '0');
  const d = String(targetDate.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function getCurrentRomaniaDay() {
  // Get day name in Romania timezone (Europe/Bucharest)
  const fmt = new Intl.DateTimeFormat('en-US', { timeZone: 'Europe/Bucharest', weekday: 'long' });
  const day = fmt.format(new Date()).toLowerCase();
  const map = { monday: 'luni', tuesday: 'marti', wednesday: 'miercuri', thursday: 'joi', friday: 'vineri' };
  return map[day] || 'luni';
}

const ClientHome = () => {
  const navigate = useNavigate();
  const [menuItems, setMenuItems] = useState([]);
  const [cart, setCart] = useState(() => {
    try {
      const saved = localStorage.getItem('g8_cart');
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });
  const [selectedDay, setSelectedDay] = useState(getCurrentRomaniaDay());
  const [selectedCategory, setSelectedCategory] = useState('Oferta zilei');
  const [loading, setLoading] = useState(true);
  const [comboItem, setComboItem] = useState(null);
  const [comboSelections, setComboSelections] = useState({});
  const autoSwitchedRef = useRef(false);
  const [ofertaZileiStatus, setOfertaZileiStatus] = useState(() => getOfertaZileiStatus(getCurrentRomaniaDay()));
  const [countdowns, setCountdowns] = useState({});
  const cartRef = useRef(cart);

  // Persist cart to localStorage on every change — restores automatically on back-navigation
  useEffect(() => {
    localStorage.setItem('g8_cart', JSON.stringify(cart));
    cartRef.current = cart;
  }, [cart]);

  // On mount: "unpause" any reservations frozen while user was on Checkout page
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    setCart(prev => prev.map(item => {
      if (item.reservation_id && item.pause_started_at) {
        const paused_duration = Date.now() - item.pause_started_at;
        const new_total = (item.total_paused_ms || 0) + paused_duration;
        const { pause_started_at, ...rest } = item;
        return { ...rest, total_paused_ms: new_total };
      }
      return item;
    }));
  }, []); // runs once on mount

  // 1-second countdown ticker for cart reservations — runs once, reads via cartRef
  useEffect(() => {
    const tick = () => {
      const currentCart = cartRef.current;
      const now = Date.now();
      const updates = {};
      const toRelease = [];

      currentCart.forEach(item => {
        if (!item.reservation_id) return;
        const elapsed = now - item.reserved_at - (item.total_paused_ms || 0);
        const remaining = Math.max(0, RESERVATION_DISPLAY_SECONDS - Math.floor(elapsed / 1000));
        updates[item.id] = remaining;
        if (remaining === 0) toRelease.push(item);
      });

      setCountdowns(updates);

      if (toRelease.length > 0) {
        toRelease.forEach(item => {
          axios.delete(`${API}/cart/release/${item.reservation_id}`).catch(() => {});
        });
        setCart(prev => prev.filter(item => !toRelease.some(r => r.id === item.id)));
        toast.warning('Rezervarea a expirat. Adaugă din nou produsul dacă mai este disponibil.', {
          id: 'g8-reservation-expired',
          duration: 5000,
        });
      }
    };

    const iv = setInterval(tick, 1000);
    return () => clearInterval(iv);
  }, []); // runs once; accesses latest cart via cartRef

  // Re-evaluate status whenever selectedDay changes OR every 30s (for today's live cutoff tick)
  useEffect(() => {
    setOfertaZileiStatus(getOfertaZileiStatus(selectedDay));
    const iv = setInterval(() => setOfertaZileiStatus(getOfertaZileiStatus(selectedDay)), 30000);
    return () => clearInterval(iv);
  }, [selectedDay]);

  useEffect(() => {
    fetchMenu();
    const interval = setInterval(fetchMenu, 5000);
    return () => clearInterval(interval);
  }, [selectedDay, selectedCategory]);

  // If today's pickup cutoff has passed, auto-switch to tomorrow and drop today-only cart items
  useEffect(() => {
    const checkCutoff = async () => {
      if (autoSwitchedRef.current) return;
      if (selectedDay !== getCurrentRomaniaDay()) return;
      try {
        const todayDate = getDateForDay(selectedDay);
        const { data } = await axios.get(`${API}/pickup-slots?date=${todayDate}`);
        if (data.length > 0 && !data.some(s => s.available)) {
          autoSwitchedRef.current = true;
          setCart(prevCart => prevCart.filter(item => {
            if (item.recurring) return true;
            // Keep items with an active (non-expired) reservation — they manage their own lifecycle
            if (item.reservation_id && item.reserved_at) {
              const elapsed = Date.now() - item.reserved_at - (item.total_paused_ms || 0);
              if (elapsed < RESERVATION_DISPLAY_SECONDS * 1000) return true;
              // Reservation has already expired — release and drop
              axios.delete(`${API}/cart/release/${item.reservation_id}`).catch(() => {});
            }
            return false;
          }));
          toast.info('Programul pentru astăzi s-a încheiat. Am actualizat meniul pentru Mâine.', {
            id: 'g8-day-cutoff',
            duration: 6000,
          });
          const idx = daysOfWeek.findIndex(d => d.value === selectedDay);
          setSelectedDay(daysOfWeek[(idx + 1) % daysOfWeek.length].value);
        }
      } catch (error) {
        console.error('Error checking pickup cutoff:', error);
      }
    };
    checkCutoff();
    const iv = setInterval(checkCutoff, 60000);
    return () => clearInterval(iv);
  }, [selectedDay]);

  const fetchMenu = async () => {
    try {
      const date = getDateForDay(selectedDay);
      let url = `${API}/menu?date=${date}`;
      if (selectedCategory) {
        url += `&category=${selectedCategory}`;
      }
      const { data } = await axios.get(url);
      setMenuItems(data);
    } catch (error) {
      console.error('Error fetching menu:', error);
    } finally {
      setLoading(false);
    }
  };

  const addToCart = async (item) => {
    // If item has option groups (combo), open the modal instead
    if (item.is_combo && (item.option_groups || []).length > 0) {
      setComboItem(item);
      setComboSelections({});
      return;
    }
    const existingItem = cart.find(c => c.id === item.id);
    const unlimited = item.stock === null || item.stock === undefined;
    const isLimited = !unlimited && !item.recurring;

    if (existingItem) {
      if (unlimited || existingItem.quantity < item.stock) {
        setCart(cart.map(c => c.id === item.id ? { ...c, quantity: c.quantity + 1 } : c));
      }
    } else {
      if (unlimited || item.stock > 0) {
        if (isLimited) {
          // Non-recurring + limited stock: reserve via backend before adding to cart
          try {
            const { data } = await axios.post(`${API}/cart/reserve`, {
              menu_item_id: item.id,
              product_id: item.product_id,
              quantity: 1,
            });
            setCart(prev => [...prev, {
              ...item,
              quantity: 1,
              reservation_id: data.reservation_id,
              reserved_at: Date.now(),
              total_paused_ms: 0,
            }]);
          } catch (err) {
            toast.error(err.response?.data?.detail || 'Produsul nu mai este disponibil pentru rezervare.');
          }
        } else {
          setCart([...cart, { ...item, quantity: 1 }]);
        }
      }
    }
  };

  const toggleOption = (groupIdx, opt, multi) => {
    setComboSelections(prev => {
      const next = { ...prev };
      if (multi) {
        const current = Array.isArray(next[groupIdx]) ? [...next[groupIdx]] : [];
        const i = current.findIndex(o => o.name === opt.name);
        if (i >= 0) current.splice(i, 1);
        else current.push(opt);
        next[groupIdx] = current;
      } else {
        next[groupIdx] = [opt];
      }
      return next;
    });
  };

  const isComboValid = useMemo(() => {
    if (!comboItem) return false;
    return (comboItem.option_groups || []).every((g, gi) => {
      if (g.required === false) return true;
      const sel = comboSelections[gi];
      return Array.isArray(sel) && sel.length > 0;
    });
  }, [comboItem, comboSelections]);

  const comboTotalExtra = useMemo(() => {
    if (!comboItem) return 0;
    let total = 0;
    Object.values(comboSelections).forEach(sel => {
      (sel || []).forEach(o => { total += parseFloat(o.extra_price) || 0; });
    });
    return total;
  }, [comboItem, comboSelections]);

  const confirmComboToCart = () => {
    if (!comboItem || !isComboValid) return;
    const choices = (comboItem.option_groups || []).map((g, gi) => ({
      group_title: g.title,
      selections: comboSelections[gi] || []
    })).filter(c => c.selections.length > 0);
    const finalPrice = parseFloat(comboItem.price) + comboTotalExtra;
    // Each combo with specific selections becomes a unique cart line (timestamp-based id)
    const cartLine = {
      ...comboItem,
      cart_line_id: `${comboItem.id}_${Date.now()}`,
      id: `${comboItem.id}_${Date.now()}`,
      price: finalPrice,
      base_price: comboItem.price,
      quantity: 1,
      choices,
    };
    setCart([...cart, cartLine]);
    setComboItem(null);
    setComboSelections({});
  };

  const removeFromCart = (itemId) => {
    const existingItem = cart.find(c => c.id === itemId);
    if (!existingItem) return;
    if (existingItem.quantity === 1) {
      // Full removal — release the backend reservation if any
      if (existingItem.reservation_id) {
        axios.delete(`${API}/cart/release/${existingItem.reservation_id}`).catch(() => {});
      }
      setCart(cart.filter(c => c.id !== itemId));
    } else {
      setCart(cart.map(c => c.id === itemId ? { ...c, quantity: c.quantity - 1 } : c));
    }
  };

  const getItemQuantityInCart = (itemId) => {
    const item = cart.find(c => c.id === itemId);
    return item ? item.quantity : 0;
  };

  const getTotalAmount = () => {
    return cart.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  };

  // Pause all active reservations before navigating to Checkout
  const handleGoToCheckout = () => {
    const now = Date.now();
    const pausedCart = cart.map(item =>
      item.reservation_id ? { ...item, pause_started_at: now } : item
    );
    // Write directly so localStorage is updated before React re-renders
    localStorage.setItem('g8_cart', JSON.stringify(pausedCart));
    navigate('/checkout', { state: { cart: pausedCart, selectedDay } });
  };

  return (
    <div className="app-container">
      <header className="sticky top-0 z-50 bg-white/95 border-b border-[#F0F0F0] backdrop-blur-md">
        <div className="px-6 py-4">
          <div className="flex justify-between items-center">
            <h1 className="brand-title text-2xl text-[#2D3748] text-center flex-1">
              Garden<span className="text-[#7BA05B]">8</span>
            </h1>
            <button
              data-testid="cart-button"
              onClick={handleGoToCheckout}
              className="relative p-2 rounded-full hover:bg-[#F8F9FA] transition-all duration-200 ml-2"
            >
              <ShoppingCart className="w-5 h-5 text-[#4A5568]" />
              {cart.length > 0 && (
                <span className="absolute -top-1 -right-1 bg-[#7BA05B] text-white text-[10px] w-4 h-4 rounded-full flex items-center justify-center font-bold shadow-md">
                  {cart.length}
                </span>
              )}
            </button>
          </div>
        </div>
      </header>

      <main className="px-3 sm:px-6 py-5">
        <div className="flex gap-0.5 justify-between pb-4 mb-2 border-b border-[#F0F0F0]" data-testid="day-selector">
          {daysOfWeek.map((day) => (
            <button
              key={day.value}
              data-testid={`day-tab-${day.value}`}
              onClick={() => setSelectedDay(day.value)}
              className={`day-tab flex-1 ${selectedDay === day.value ? 'active' : ''}`}
            >
              {day.label}
            </button>
          ))}
        </div>

        <div className="flex gap-2 overflow-x-auto pb-4 mb-2 hide-scrollbar" data-testid="category-selector">
          {categories.map((cat) => (
            <button
              key={cat.value}
              data-testid={`category-tab-${cat.value}`}
              onClick={() => setSelectedCategory(cat.value)}
              className={`category-tab ${selectedCategory === cat.value ? 'active' : ''}`}
            >
              {cat.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="text-center py-16">
            <p className="text-[#A0AEC0] text-sm font-medium">Se încarcă meniul...</p>
          </div>
        ) : menuItems.length === 0 ? (
          <div className="text-center py-16 px-4 bg-[#F8F9FA] border border-[#E2E8F0] rounded-2xl mx-auto mt-4">
            <Utensils className="w-8 h-8 text-[#CBD5E0] mx-auto mb-3" />
            <p className="text-[#718096] text-sm font-medium">Nu există meniu disponibil pentru această selecție.</p>
          </div>
        ) : (
          <div className="menu-list">
            {menuItems.map((item) => {
              const quantityInCart = getItemQuantityInCart(item.id);
              const unlimited = item.stock === null || item.stock === undefined;
              const availableStock = unlimited ? Infinity : item.stock - quantityInCart;
              const isSoldOut = !unlimited && item.stock <= 0;
              
              return (
                <div
                  key={item.id}
                  data-testid={`menu-item-${item.id}`}
                  className="minimalist-card"
                >
                  {/* Dish illustration (AI-generated or uploaded) */}
                  {item.has_image ? (
                    <div className="shrink-0 self-start">
                      <img
                        src={`${API}/product-image/${item.product_id}`}
                        alt={item.name}
                        className="w-20 h-20 sm:w-24 sm:h-24 rounded-xl object-cover border border-[#E8E8E8]"
                        loading="lazy"
                      />
                    </div>
                  ) : (
                    <div className="category-icon">
                      {getCategoryIcon(item.category)}
                    </div>
                  )}
                  
                  <div className="flex-1 min-w-0">
                    <h3 className="text-[15px] font-semibold text-[#2D3748] leading-tight mb-0.5">
                      {item.name}
                    </h3>
                    {item.description && (
                      <p className="text-[12px] text-[#718096] leading-snug mb-1 line-clamp-2" data-testid={`menu-item-description-${item.id}`}>
                        {item.description}
                      </p>
                    )}
                    {/* Low-stock urgency badge: hidden above threshold, visible only when ≤ STOCK_URGENCY_THRESHOLD */}
                    {!unlimited && !isSoldOut && availableStock > 0 && availableStock <= STOCK_URGENCY_THRESHOLD && (
                      <span
                        data-testid={`low-stock-badge-${item.id}`}
                        className="stock-badge low"
                      >
                        Mai {availableStock === 1 ? 'e' : 'sunt'} {availableStock} {availableStock === 1 ? 'porție' : 'porții'}!
                      </span>
                    )}
                  </div>

                  <div className="flex flex-col items-end gap-2">
                    <p className="text-[15px] font-bold text-[#2D3748] whitespace-nowrap">
                      {item.price.toFixed(2)} lei
                    </p>
                    
                    {quantityInCart > 0 && !item.is_combo ? (
                      <div className="flex flex-col items-end gap-1">
                        {(() => {
                          const cartItem = cart.find(c => c.id === item.id);
                          return cartItem?.reservation_id && countdowns[item.id] !== undefined ? (
                            <span
                              data-testid={`reservation-countdown-${item.id}`}
                              className={`text-[10px] font-semibold rounded-full px-2 py-0.5 ${
                                countdowns[item.id] <= 60
                                  ? 'text-red-600 bg-red-50 border border-red-200'
                                  : 'text-amber-600 bg-amber-50 border border-amber-200'
                              }`}
                            >
                              {formatCountdown(countdowns[item.id])}
                            </span>
                          ) : null;
                        })()}
                        <div className="quantity-control">
                          <button
                            data-testid={`decrease-quantity-${item.id}`}
                            onClick={() => removeFromCart(item.id)}
                            className="quantity-btn"
                          >
                            −
                          </button>
                          <span className="text-[13px] font-bold text-[#2D3748] min-w-[20px] text-center">
                            {quantityInCart}
                          </span>
                          <button
                            data-testid={`increase-quantity-${item.id}`}
                            onClick={() => addToCart(item)}
                            disabled={availableStock <= 0}
                            className="quantity-btn"
                          >
                            +
                          </button>
                        </div>
                      </div>
                    ) : (() => {
                      const isOfertaZilei = item.category === 'Oferta zilei';
                      const status = isOfertaZilei ? ofertaZileiStatus : null;
                      const ofertaClosed = isOfertaZilei && !status.canOrder;
                      const disabled = isSoldOut || ofertaClosed;
                      return (
                        <div className="flex flex-col items-end gap-1.5">
                          {/* Oferta zilei badge */}
                          {isOfertaZilei && (
                            <span
                              data-testid={`oferta-zilei-badge-${item.id}`}
                              className={`text-[10px] font-semibold px-2 py-0.5 rounded-full whitespace-nowrap ${
                                status.badgeType === 'info'
                                  ? 'bg-[#E8F5E9] text-[#2E7D32]'
                                  : status.badgeType === 'warning'
                                  ? 'bg-amber-50 text-amber-700 border border-amber-200'
                                  : 'bg-[#FEF2F2] text-[#EF4444]'
                              }`}
                            >
                              {status.badgeText}
                            </span>
                          )}
                          <button
                            data-testid={`add-to-cart-${item.id}`}
                            onClick={() => !disabled && addToCart(item)}
                            disabled={disabled}
                            className={`accent-button ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                          >
                            {isSoldOut ? 'Epuizat azi' : ofertaClosed ? 'Închis' : (item.is_combo ? 'Alege' : 'Adaugă')}
                          </button>
                        </div>
                      );
                    })()}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {cart.length > 0 && (
          <div className="mt-8 pb-24">
            {/* Spacer for fixed bottom bar */}
          </div>
        )}
      </main>

      {cart.length > 0 && (
        <div
          className="fixed bottom-0 bg-white/95 border-t border-[#F0F0F0] shadow-[0_-8px_30px_rgba(0,0,0,0.08)] z-50 backdrop-blur-md"
          style={{ paddingBottom: 'env(safe-area-inset-bottom)', left: '50%', transform: 'translateX(-50%)', width: '100%', maxWidth: 600 }}
          data-testid="cart-summary-bar"
        >
          <div className="px-6 py-4 flex items-center justify-between">
            <div>
              <p className="text-[11px] font-bold text-[#718096] uppercase tracking-wider mb-0.5">Total comandă</p>
              <p className="text-xl font-bold text-[#2D3748]">{getTotalAmount().toFixed(2)} <span className="text-sm font-semibold text-[#718096]">lei</span></p>
            </div>
            <button
              data-testid="proceed-checkout-button"
              onClick={handleGoToCheckout}
              className="accent-button px-8 py-3 text-[15px]"
            >
              Finalizează
            </button>
          </div>
        </div>
      )}

      {comboItem && (
        <div className="combo-modal-overlay" onClick={() => setComboItem(null)} data-testid="combo-modal">
          <div className="combo-modal" onClick={(e) => e.stopPropagation()}>
            <div className="combo-modal-header">
              <div>
                <h2 className="combo-modal-title">{comboItem.name}</h2>
                {comboItem.description && (
                  <p className="text-[13px] text-[#718096] mt-0.5">{comboItem.description}</p>
                )}
                <p className="text-[13px] text-[#5C8945] font-semibold mt-1">
                  {parseFloat(comboItem.price).toFixed(2)} lei
                </p>
              </div>
              <button
                data-testid="combo-modal-close"
                onClick={() => setComboItem(null)}
                className="combo-modal-close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="combo-modal-body">
              {(comboItem.option_groups || []).map((group, gi) => {
                const selected = comboSelections[gi] || [];
                return (
                  <div key={gi} className="combo-group" data-testid={`combo-group-${gi}`}>
                    <div className="combo-group-header">
                      <h3 className="combo-group-title">{group.title}</h3>
                      <span className="combo-group-hint">
                        {group.required !== false ? 'Obligatoriu' : 'Opțional'}
                        {group.multi ? ' · multiple' : ''}
                      </span>
                    </div>
                    <div className="combo-options">
                      {(group.options || []).map((opt, oi) => {
                        const isSelected = selected.some(o => o.name === opt.name);
                        const extra = parseFloat(opt.extra_price) || 0;
                        return (
                          <button
                            key={oi}
                            data-testid={`combo-option-${gi}-${oi}`}
                            onClick={() => toggleOption(gi, opt, !!group.multi)}
                            className={`combo-option ${isSelected ? 'selected' : ''}`}
                          >
                            <span className="combo-option-radio">
                              {isSelected && <Check className="w-3.5 h-3.5" />}
                            </span>
                            <span className="combo-option-name">{opt.name}</span>
                            <span className={`combo-option-price ${extra > 0 ? 'extra' : 'free'}`}>
                              {extra > 0 ? `+${extra.toFixed(2)} lei` : 'inclus'}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="combo-modal-footer">
              <div className="combo-modal-total">
                <span className="text-[13px] text-[#718096]">Total</span>
                <span className="text-[18px] font-bold text-[#2D3748]">
                  {(parseFloat(comboItem.price) + comboTotalExtra).toFixed(2)} lei
                </span>
              </div>
              <Button
                data-testid="combo-add-to-cart"
                onClick={confirmComboToCart}
                disabled={!isComboValid}
                className="combo-confirm-btn"
              >
                Adaugă în coș
              </Button>
            </div>
          </div>
        </div>
      )}
      <Footer />
    </div>
  );
};

export default ClientHome;
